import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from 'store';

import { motion, Variants } from 'framer-motion';
import { io } from 'socket.io-client';
import { SOCKET_API_URL } from '@config';

import { LastDrop } from 'models/types/stats-socket';
import { setLastDrop, updateLastDrop } from 'store/slices/stats/lastDrops';
import SteamImage from 'components/SteamImage';
import { Avatar, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { getCaseImageUrl } from 'utils/outerImages';
import VerifiedIcon from 'assets/icon24/Verified.svg';

import { ROULETTE_SPEED } from 'views/Case/components/Roulette/constants';

const LAST_CASE_OPEN = 'last_case_open';
const LAST_WIN_ITEM = 'last_win_item';

const statsSocket = io(`${SOCKET_API_URL}/stats`, {
  transports: ['websocket', 'pooling']
});

const variants: Variants = {
  initial: (i: number) => ({
    width: 0
    // opacity: 0,
    // x: -90 * i,
    // zIndex: -1
  }),
  animate: (i: number) => ({
    width: 'auto',
    transition: { type: 'tween' }
    // opacity: 1,
    // x: 0,
    // zIndex: 1,
    // transition: {
    //   delay: i * 0.04
    // }
  })
};

const DropsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { lastDrop } = useAppSelector((state) => state.lastDrops);

  useEffect(() => {
    statsSocket.emit(LAST_CASE_OPEN, (data: LastDrop[]) => {
      dispatch(setLastDrop(data));
    });

    statsSocket.on(LAST_WIN_ITEM, (data: { lastDrop: LastDrop[] }) => {
      const isBattleCase = data?.lastDrop?.[0]?.type === 'CASE_BATTLE';
      const timePerRound = isBattleCase ? ROULETTE_SPEED.battle : ROULETTE_SPEED.normal;
      const timingByType = isBattleCase
        ? (timePerRound * data?.lastDrop?.length) / 2
        : timePerRound;
      const time = timingByType * 1000;
      const lastDropData = data?.lastDrop || [];

      setTimeout(() => {
        lastDropData?.map((item, index) => {
          return setTimeout(() => dispatch(updateLastDrop([item])), (index + 1) * 500);
        });
      }, time);
    });

    return () => {
      statsSocket.close();
    };
  }, []);

  return (
    <Box width={1} display='flex' overflow='hidden' position='relative'>
      <Box width={1} display='flex' overflow='hidden' gap={0.5}>
        {lastDrop?.length > 0 ? (
          <DList list={lastDrop} />
        ) : (
          <ItemsSkeleton2
            containerSx={{ flexWrap: 'nowrap' }}
            spacing={0.5}
            height='145px'
            phxs={'auto'}
            count={12}
            width={{ phxs: 116, phlg: 146 }}
            height={{ phxs: 90, phlg: 100 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default DropsList;

const DList = ({ list = [] }) => {
  return (
    <>
      {list?.map((item, index) => (
        <Box
          component={motion.div}
          display='flex'
          position='relative'
          overflow='hidden'
          variants={variants}
          initial='initial'
          animate='animate'
          custom={index}
          key={`last-drop-item-${item?.id}`}
          layout
        >
          <DropItemNew drop={item} />
          {/* <DropItem drop={item} userId={item?.user?.id} /> */}
        </Box>
      ))}
      {/* <LastDropItems lastDrop={lastDrop} /> */}
    </>
  );
};

//-----------------------

const itemVariant = {
  initial: {
    y: 0
  },
  hover: {
    y: -100
  }
};
const userVariant = {
  initial: {
    x: -146
  },
  hover: {
    x: 0
  }
};
const caseVariant = {
  initial: {
    x: 146
  },
  hover: {
    x: 0
  }
};

const DropItemNew = ({ drop }) => {
  return (
    <Link href={`/profile/user/${drop?.user?.id}`} passHref>
      <Box
        display='flex'
        position='relative'
        width={{ phxs: 116, phlg: 146 }}
        height={{ phxs: 90, phlg: 100 }}
        sx={(theme) => ({
          background: theme.palette?.rarityGradients[drop.item?.rarityName || 'default'],
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[700]}`
        })}
        component={motion.div}
        initial='initial'
        whileHover='hover'
        transition={{
          staggerChildren: 0.05
        }}
        overflow='hidden'
      >
        {/* ITEM */}
        <Box
          component={motion.div}
          variants={itemVariant}
          width={1}
          height={'100%'}
          position='absolute'
          display='flex'
          justifyContent='center'
          p={1}
        >
          <ItemT item={drop?.item} />
        </Box>
        <Box width={1} height={100} position='absolute' component={motion.div}>
          {/* USER */}
          <Box
            component={motion.div}
            variants={userVariant}
            width={1}
            height={'100%'}
            position='absolute'
            display='flex'
            justifyContent='center'
            zIndex={1}
            p={1}
            transition={{
              bounce: 1,
              duration: 0.2
            }}
          >
            <UserT user={drop?.user} dropType={drop?.type} ratio={drop?.ratio} />
          </Box>
          {/* CASE */}
          <Box
            component={motion.div}
            variants={caseVariant}
            width={1}
            height={'100%'}
            position='absolute'
            display='flex'
            justifyContent='center'
            // p={0.5}
            transition={{
              bounce: 1,
              duration: 0.2
            }}
          >
            <CaseT item={drop?.case} dropType={drop?.type} />
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

// import UpgraderImage from 'assets/lastdrops/upgrader.png';
import UpgraderImage from 'assets/lastdrops/upgrader.webp';

import CasesIcon from 'oldAssets/header/Cases.svg';
import BattleIcon from 'oldAssets/header/Battle.svg';
import UpgradeIcon from 'oldAssets/header/Upgrade.svg';
import ItemsSkeleton2 from 'components/ItemsSkeleton2';

const DROP_TYPES = {
  CASE: {
    name: 'CASE',
    label: 'Case',
    icon: <CasesIcon />,
    bgImg: ''
  },
  CASE_BATTLE: {
    name: 'CASE_BATTLE',
    label: 'Case Battle',
    icon: <BattleIcon />,
    bgImg: ''
  },
  UPGRADER: {
    name: 'UPGRADER',
    label: 'Upgrader',
    icon: <UpgradeIcon />,
    bgImg: UpgraderImage
  }
};

const CaseT = ({ item, dropType = 'CASE' }) => {
  const imgKey = dropType === DROP_TYPES.UPGRADER.name ? 'lastdrop-upgrader' : item?.itemName;

  const cImage =
    dropType === DROP_TYPES.UPGRADER.name
      ? DROP_TYPES.UPGRADER.bgImg
      : item?.caseImg
      ? getCaseImageUrl(item?.caseImg)
      : '';

  const rImage = item?.rifleImg ? getCaseImageUrl(item?.rifleImg, true) : '';

  // const rImage = item?.rifleImg ? getCaseImageUrl(item?.rifleImg, true) : '';

  return (
    <Box display='flex' position='relative' width={1} height={1}>
      <Box
        position='absolute'
        bottom={0}
        right={-45}
        display='flex'
        flexDirection='column'
        width={1}
        height={1}
      >
        {cImage && (
          <Image
            unoptimized
            //
            // unoptimized
            // quality={100}
            // loading='eager'
            key={`next-case-image-${imgKey}`}
            src={cImage}
            alt={'drop-list'}
            fill
            // sizes='99vw'
            // layout='fill'
            // objectFit='contain'
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            // quality={50}
          />
        )}
        {rImage && (
          <Image
            unoptimized
            //
            // unoptimized
            // quality={100}
            // loading='eager'
            key={`next-rifle-image-${imgKey}`}
            src={rImage}
            alt={'drop-list'}
            fill
            // sizes='99vw'
            // layout='fill'
            // objectFit='contain'
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            // quality={50}
          />
        )}
      </Box>
    </Box>
  );
};

const UserT = ({ user, dropType = 'CASE', ratio }) => {
  return (
    <Box display='flex' position='relative' width={1} height={1}>
      <Box display='flex' position='absolute' alignItems='center' top={0} left={0} gap={1 / 4}>
        <Box sx={{ svg: { height: 12, width: 12 }, path: { fill: 'white' } }} display='flex'>
          {DROP_TYPES[dropType]?.icon}
        </Box>
        <Typography variant='overline' textTransform='capitalize'>
          {DROP_TYPES[dropType]?.label}
        </Typography>

        {ratio ? (
          <Typography variant='overline' color={(theme) => theme.palette.grey[300]}>
            {Number(ratio)?.toFixed(1)}%
          </Typography>
        ) : null}
      </Box>
      <Box position='absolute' bottom={0} left={0} display='flex' flexDirection='column' gap={0.5}>
        <Avatar
          alt={user?.name}
          src={user?.avatarFull}
          variant='rounded'
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            cursor: 'pointer'
          }}
        />
        <Box display={'flex'} alignItems={'flex-end'} gap={1}>
          <Typography
            variant='caption'
            sx={{
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 100
            }}
          >
            {user?.name}
          </Typography>

          {user?.isVerified && (
            <Box display={'flex'} sx={{ svg: { width: 16, height: 16 } }}>
              <VerifiedIcon />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ItemT = ({ item }) => {
  return (
    <Box display='flex' position='relative' width={1} height={1}>
      <SteamImage itemName={item?.itemName} icon={item?.icon} width={1} height={1} />
      <Box position='absolute' bottom={0} left={0} display='flex' flexDirection='column'>
        <Typography variant='caption' color={(theme) => theme.palette.greyTransparency[400]}>
          {item?.weaponType}
        </Typography>
        <Typography variant='caption'>{item?.itemName}</Typography>
      </Box>
    </Box>
  );
};

// const LastDropItems = ({ lastDrop }) => {
//   return lastDrop?.map((item, index) => (
//     <motion.div
//       variants={variants}
//       initial='initial'
//       animate='animate'
//       custom={index}
//       key={`last-drop-item-${item?.id}`}
//     >
//       <DropItem drop={item} userId={item?.user?.id} />
//     </motion.div>
//   ));
// };
